import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import {
  Plus, Pencil, Trash2, Save, X, UserCheck, ShieldAlert,
  ChevronDown, ChevronUp, Briefcase, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERIFICATION_BADGE = {
  pending: { label: "Pendiente", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  verified: { label: "Verificado", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  rejected: { label: "Rechazado", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const REP_TYPE_LABELS = {
  // Canonical PM values (PR1) — kept first so they win the lookup.
  representante_legal: "Representante Legal",
  apoderado: "Apoderado Legal",
  // Legacy values still in DB rows from before the unification — kept
  // so old expedientes don't render as "undefined".
  power_of_attorney: "Poder Notarial",
  assembly_appointment: "Nombramiento por Asamblea",
};

const EMPTY_FORM = {
  representation_type: "", full_name: "", rfc: "", curp: "",
  nationality: "Mexicana", birth_date: "", phone: "", email: "",
  id_type: "", id_number: "", is_pep: false, pep_details: "",
};

const fc = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500";

export default function LegalRepresentatives({ customerId, readOnly = false }) {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchReps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legal-representatives?customer_id=${customerId}`, { withCredentials: true });
      setReps(res.data.items);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { fetchReps(); }, [fetchReps]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (rep) => {
    setForm({
      representation_type: rep.representation_type || "",
      full_name: rep.full_name || "",
      rfc: rep.rfc || "",
      curp: rep.curp || "",
      nationality: rep.nationality || "Mexicana",
      birth_date: rep.birth_date || "",
      phone: rep.phone || "",
      email: rep.email || "",
      id_type: rep.id_type || "",
      id_number: rep.id_number || "",
      is_pep: rep.is_pep || false,
      pep_details: rep.pep_details || "",
    });
    setEditingId(rep.representative_id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.full_name?.trim()) { toast.error("Nombre completo es obligatorio"); return; }
    if (!form.representation_type) { toast.error("Tipo de representacion es obligatorio"); return; }
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(form)) {
        cleaned[k] = typeof v === "string" && v.trim() === "" ? null : v;
      }
      if (editingId) {
        await axios.put(`${API}/legal-representatives/${editingId}`, cleaned, { withCredentials: true });
        toast.success("Representante actualizado");
      } else {
        cleaned.customer_id = customerId;
        await axios.post(`${API}/legal-representatives`, cleaned, { withCredentials: true });
        toast.success("Representante registrado");
      }
      setShowForm(false);
      setEditingId(null);
      fetchReps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar representante");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (repId) => {
    if (!window.confirm("Eliminar este representante legal?")) return;
    try {
      await axios.delete(`${API}/legal-representatives/${repId}`, { withCredentials: true });
      toast.success("Representante eliminado");
      fetchReps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="legal-representatives-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <Briefcase size={18} className="text-teal-400" />
            Representantes Legales / Apoderados
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">{reps.length} representante(s) registrado(s)</p>
        </div>
        {!readOnly && !showForm && (
          <Button onClick={openNew} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="add-representative-btn">
            <Plus size={14} className="mr-2" /> Agregar Representante
          </Button>
        )}
      </div>

      {/* No representatives warning */}
      {reps.length === 0 && !showForm && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-5 py-4 flex items-center gap-3" data-testid="no-reps-warning">
          <AlertTriangle size={18} className="text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Se requiere al menos un representante legal</p>
            <p className="text-xs text-amber-400/70 mt-0.5">
              Para verificar el KYC de una Persona Moral, debe registrar al menos un representante legal con su respectiva documentacion.
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-slate-900 border border-teal-500/30 rounded-xl p-5 space-y-4" data-testid="representative-form">
          <h3 className="text-sm font-semibold text-teal-400">
            {editingId ? "Editar Representante" : "Nuevo Representante Legal"}
          </h3>

          {/* Representation basis */}
          <div>
            <Label className="text-slate-400 text-xs">Tipo de Representacion *</Label>
            <Select value={form.representation_type} onValueChange={(v) => setForm({ ...form, representation_type: v })}>
              <SelectTrigger className={fc} data-testid="rep-type-select">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="power_of_attorney">Poder Notarial</SelectItem>
                <SelectItem value="assembly_appointment">Nombramiento por Asamblea</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Personal info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-xs">Nombre Legal Completo *</Label>
              <Input name="full_name" value={form.full_name} onChange={handleChange} className={fc} data-testid="rep-full-name" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">RFC</Label>
              <Input name="rfc" value={form.rfc} onChange={handleChange} placeholder="XXXX000000XXX" className={fc} data-testid="rep-rfc" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">CURP</Label>
              <Input name="curp" value={form.curp} onChange={handleChange} className={fc} data-testid="rep-curp" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Nacionalidad</Label>
              <Input name="nationality" value={form.nationality} onChange={handleChange} className={fc} data-testid="rep-nationality" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Fecha de Nacimiento</Label>
              <Input type="date" name="birth_date" value={form.birth_date} onChange={handleChange} className={fc} data-testid="rep-birth-date" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Telefono</Label>
              <Input name="phone" value={form.phone} onChange={handleChange} className={fc} data-testid="rep-phone" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Email</Label>
              <Input type="email" name="email" value={form.email} onChange={handleChange} className={fc} data-testid="rep-email" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Tipo de Identificacion</Label>
              <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
                <SelectTrigger className={fc} data-testid="rep-id-type">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="ine">INE/IFE</SelectItem>
                  <SelectItem value="passport">Pasaporte</SelectItem>
                  <SelectItem value="cedula">Cedula Profesional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">No. de Identificacion</Label>
              <Input name="id_number" value={form.id_number} onChange={handleChange} className={fc} data-testid="rep-id-number" />
            </div>
          </div>

          {/* PEP */}
          <div className="flex items-center gap-3">
            <input type="checkbox" id="rep_pep" checked={form.is_pep}
              onChange={(e) => setForm({ ...form, is_pep: e.target.checked })}
              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-teal-500" data-testid="rep-is-pep" />
            <Label htmlFor="rep_pep" className="text-slate-300 text-sm">Persona Politicamente Expuesta (PEP)</Label>
          </div>
          {form.is_pep && (
            <Input name="pep_details" value={form.pep_details} onChange={handleChange}
              placeholder="Cargo o funcion publica" className={fc} data-testid="rep-pep-details" />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={cancelForm} variant="outline" className="border-slate-700 text-slate-400" data-testid="cancel-rep-btn">
              <X size={14} className="mr-2" /> Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="save-rep-btn">
              <Save size={14} className="mr-2" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      )}

      {/* Representatives list */}
      {reps.map((rep) => {
        const badge = VERIFICATION_BADGE[rep.verification_status] || VERIFICATION_BADGE.pending;
        const isExpanded = expandedId === rep.representative_id;
        return (
          <div key={rep.representative_id}
            className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
            data-testid={`rep-card-${rep.representative_id}`}
          >
            {/* Summary row */}
            <div className="px-5 py-3.5 flex items-center gap-4 cursor-pointer hover:bg-slate-800/30"
              onClick={() => setExpandedId(isExpanded ? null : rep.representative_id)}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                <UserCheck size={18} className="text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-200">{rep.full_name}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${badge.cls}`}>
                    {badge.label}
                  </span>
                  {rep.is_pep && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      <ShieldAlert size={10} /> PEP
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {REP_TYPE_LABELS[rep.representation_type] || rep.representation_type}
                  {rep.rfc ? ` | RFC: ${rep.rfc}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!readOnly && (
                  <>
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-teal-400 h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); openEdit(rep); }}
                      data-testid={`edit-rep-${rep.representative_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); handleDelete(rep.representative_id); }}
                      data-testid={`delete-rep-${rep.representative_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </>
                )}
                {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
              </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-5 pb-4 border-t border-slate-800 pt-3 space-y-3">
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {[
                    ["CURP", rep.curp],
                    ["Nacionalidad", rep.nationality],
                    ["Nacimiento", rep.birth_date],
                    ["Telefono", rep.phone],
                    ["Email", rep.email],
                    ["Identificacion", rep.id_type ? `${rep.id_type} — ${rep.id_number || ""}` : null],
                    ["Riesgo", rep.risk_classification],
                    ["Screening PEP", rep.screening_result || "Pendiente"],
                    ["Screening Sanciones", rep.sanctions_result || "Pendiente"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-slate-500 text-xs">{label}</dt>
                      <dd className={`mt-0.5 ${value ? "text-slate-200" : "text-slate-600 italic"}`}>
                        {value || "Sin dato"}
                      </dd>
                    </div>
                  ))}
                </dl>
                {/* On-demand CURP download — per-subject. No bulk fan-out
                    on finalize because CapSolver is paid per call. */}
                {rep.curp && (
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`download-curp-rep-${rep.representative_id}`}
                      disabled={downloadingCurp === rep.representative_id}
                      onClick={async () => {
                        setDownloadingCurp(rep.representative_id);
                        try {
                          const r = await axios.post(
                            `${API}/curp/download-pdf/representative/${rep.representative_id}`,
                            {}, { withCredentials: true },
                          );
                          if (r.data?.success) {
                            toast.success(r.data.message || "Constancia CURP descargada");
                          } else {
                            toast.error(r.data?.message || "No se pudo descargar la CURP");
                          }
                          await fetchReps();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Error descargando CURP");
                        } finally {
                          setDownloadingCurp(null);
                        }
                      }}
                    >
                      {downloadingCurp === rep.representative_id ? "Descargando…" : "Descargar CURP"}
                    </Button>
                    {rep.curp_pdf_status === "downloaded" && (
                      <span className="text-xs text-emerald-400">PDF disponible</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
